import React, { useEffect, useState } from 'react';
import { Image, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, ActivityIndicator, Switch } from 'react-native';
import { FaChevronDown, FaChevronRight, FaCheck, FaTimes, FaUsers, FaUserPlus, FaEnvelope, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';

// Same list as LanguagesScreen
const LANGUAGES = [
  { name: "Afrikaans", code: "af" }, { name: "Albanian", code: "sq" },
  { name: "Arabic", code: "ar" }, { name: "Armenian", code: "hy" },
  { name: "Bengali", code: "bn" }, { name: "Bosnian", code: "bs" },
  { name: "Bulgarian", code: "bg" }, { name: "Catalan", code: "ca" },
  { name: "Chinese (Simplified)", code: "zh-CN" }, { name: "Chinese (Traditional)", code: "zh-TW" },
  { name: "Croatian", code: "hr" }, { name: "Czech", code: "cs" },
  { name: "Danish", code: "da" }, { name: "Dutch", code: "nl" },
  { name: "English", code: "en" }, { name: "Estonian", code: "et" },
  { name: "Filipino", code: "tl" }, { name: "Finnish", code: "fi" },
  { name: "French", code: "fr" }, { name: "Georgian", code: "ka" },
  { name: "German", code: "de" }, { name: "Greek", code: "el" },
  { name: "Hebrew", code: "he" }, { name: "Hindi", code: "hi" },
  { name: "Hungarian", code: "hu" }, { name: "Indonesian", code: "id" },
  { name: "Irish", code: "ga" }, { name: "Italian", code: "it" },
  { name: "Japanese", code: "ja" }, { name: "Korean", code: "ko" },
  { name: "Latvian", code: "lv" }, { name: "Lithuanian", code: "lt" },
  { name: "Malay", code: "ms" }, { name: "Norwegian", code: "no" },
  { name: "Persian", code: "fa" }, { name: "Polish", code: "pl" },
  { name: "Portuguese", code: "pt" }, { name: "Romanian", code: "ro" },
  { name: "Russian", code: "ru" }, { name: "Serbian", code: "sr" },
  { name: "Slovak", code: "sk" }, { name: "Spanish", code: "es" },
  { name: "Swahili", code: "sw" }, { name: "Swedish", code: "sv" },
  { name: "Thai", code: "th" }, { name: "Turkish", code: "tr" },
  { name: "Ukrainian", code: "uk" }, { name: "Urdu", code: "ur" },
  { name: "Vietnamese", code: "vi" }, { name: "Welsh", code: "cy" },
];

const BRAND_SCALES = [
  { value: 'local',    labelKey: 'app.org.scaleLocal',    descKey: 'app.org.scaleLocalDesc' },
  { value: 'national', labelKey: 'app.org.scaleNational', descKey: 'app.org.scaleNationalDesc' },
  { value: 'global',   labelKey: 'app.org.scaleGlobal',   descKey: 'app.org.scaleGlobalDesc' },
];

const COUNTRIES = [
  "Global",
  "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile",
  "China", "Colombia", "Czech Republic", "Denmark", "Egypt", "Finland", "France",
  "Germany", "Greece", "Hong Kong", "Hungary", "India", "Indonesia", "Ireland",
  "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico", "Morocco",
  "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru",
  "Philippines", "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia",
  "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland",
  "Taiwan", "Thailand", "Turkey", "UAE", "Ukraine", "United Kingdom",
  "United States", "Vietnam",
];

const ROLES = [
  { value: 'viewer', labelKey: 'app.org.roleViewer' },
  { value: 'member', labelKey: 'app.org.roleMember' },
  { value: 'admin',  labelKey: 'app.org.roleAdmin' },
  { value: 'owner',  labelKey: 'app.org.roleOwner' },
];

export default function OrgSettingsScreen() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg, myRole, refreshOrgs } = useOrg();

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [langSearch, setLangSearch] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [competitorsOpen, setCompetitorsOpen] = useState(false);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorForm, setCompetitorForm] = useState(null);
  const [editingCompetitorId, setEditingCompetitorId] = useState(null);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [escalationConfig, setEscalationConfig] = useState({
    min_followers_for_negative_escalation: 50000,
    min_sentiment_for_negative_escalation: -0.5,
    viral_reach_tiers: ['large', 'viral'],
    pattern_cluster_min_count: 5,
    pattern_cluster_hours_window: 12,
  });
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [escalationMsg, setEscalationMsg] = useState('');
  const [keywordAlerts, setKeywordAlerts] = useState([]);
  const [keywordAlertsOpen, setKeywordAlertsOpen] = useState(false);
  const [keywordAlertsLoading, setKeywordAlertsLoading] = useState(false);
  const [newAlertKeyword, setNewAlertKeyword] = useState('');
  const [newAlertSubscribers, setNewAlertSubscribers] = useState([]);
  const [newAlertOnlyImportant, setNewAlertOnlyImportant] = useState(false);
  const [editingAlertId, setEditingAlertId] = useState(null);
  const [editingAlertSubscribers, setEditingAlertSubscribers] = useState([]);
  const [tvTerms, setTvTerms] = useState([]);
  const [tvLists, setTvLists] = useState([]);
  const [tvTermsOpen, setTvTermsOpen] = useState(false);
  const [tvTermsLoading, setTvTermsLoading] = useState(false);
  const [newTvTerm, setNewTvTerm] = useState('');
  const [newTvTermCategory, setNewTvTermCategory] = useState('competitor');
  const [newTvTermListId, setNewTvTermListId] = useState('');
  const [editingTvTermId, setEditingTvTermId] = useState(null);
  const [editingTvTermText, setEditingTvTermText] = useState('');
  const [tvSelectMode, setTvSelectMode] = useState(false);
  const [tvSelected, setTvSelected] = useState([]);

  const canEdit = myRole === 'owner' || myRole === 'admin';
  const canManage = canEdit;

  const loadMembers = () => {
    if (!activeOrg) return;
    setMembersLoading(true);
    Promise.all([
      OrgManager.listMembers(activeOrg.id),
      canManage ? OrgManager.listInvitations(activeOrg.id) : Promise.resolve([]),
    ])
      .then(([m, inv]) => { setMembers(m || []); setInvitations(inv || []); })
      .catch(e => console.error('OrgSettingsScreen members error:', e))
      .finally(() => setMembersLoading(false));
  };

  useEffect(() => { loadMembers(); }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = () => {
    if (!activeOrg) return;
    OrgManager.listTags(activeOrg.id)
      .then(t => setTags(t || []))
      .catch(e => console.error('Failed to load tags:', e));
  };
  useEffect(() => { loadTags(); }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTag = async () => {
    if (!newTagName.trim() || !activeOrg) return;
    setTagsLoading(true);
    try {
      await OrgManager.createTag(activeOrg.id, newTagName.trim());
      setNewTagName('');
      loadTags();
    } catch (e) {
      alert(`Failed to create tag: ${e.message}`);
    } finally {
      setTagsLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!activeOrg) return;
    try {
      await OrgManager.deleteTag(activeOrg.id, tagId);
      loadTags();
    } catch (e) {
      alert(`Failed to delete tag: ${e.message}`);
    }
  };

  const loadCompetitors = () => {
    if (!activeOrg) return;
    OrgManager.listCompetitors(activeOrg.id)
      .then(c => setCompetitors(c || []))
      .catch(e => console.error('Failed to load competitors:', e));
  };
  useEffect(() => { loadCompetitors(); }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveCompetitor = async () => {
    if (!competitorForm || !competitorForm.name?.trim() || !activeOrg) return;
    setCompetitorsLoading(true);
    try {
      const data = {
        name: competitorForm.name.trim(),
        logo_url: competitorForm.logo_url?.trim() || null,
        description: competitorForm.description?.trim() || null,
        keywords: competitorForm.keywords_text
          ? competitorForm.keywords_text.split(',').map(k => k.trim()).filter(Boolean)
          : [],
      };
      if (editingCompetitorId) {
        await OrgManager.updateCompetitor(activeOrg.id, editingCompetitorId, data);
      } else {
        await OrgManager.createCompetitor(activeOrg.id, data);
      }
      setCompetitorForm(null);
      setEditingCompetitorId(null);
      loadCompetitors();
    } catch (e) {
      alert(`Failed to save competitor: ${e.message}`);
    } finally {
      setCompetitorsLoading(false);
    }
  };

  const handleEditCompetitor = (comp) => {
    setEditingCompetitorId(comp.id);
    setCompetitorForm({
      name: comp.name,
      logo_url: comp.logo_url || '',
      description: comp.description || '',
      keywords_text: (comp.keywords || []).join(', '),
    });
  };

  const handleDeleteCompetitor = async (compId, compName) => {
    if (!window.confirm(`Delete competitor "${compName}"?`)) return;
    try {
      await OrgManager.deleteCompetitor(activeOrg.id, compId);
      loadCompetitors();
    } catch (e) {
      alert(`Failed to delete competitor: ${e.message}`);
    }
  };

  const loadKeywordAlerts = () => {
    if (!activeOrg) return;
    OrgManager.listKeywordAlerts(activeOrg.id)
      .then(a => setKeywordAlerts(a || []))
      .catch(e => console.error('Failed to load keyword alerts:', e));
  };
  useEffect(() => { loadKeywordAlerts(); }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddKeywordAlert = async () => {
    if (!newAlertKeyword.trim() || !activeOrg || newAlertSubscribers.length === 0) return;
    setKeywordAlertsLoading(true);
    try {
      await OrgManager.createKeywordAlert(activeOrg.id, {
        keyword: newAlertKeyword.trim(),
        subscriber_ids: newAlertSubscribers,
        only_important_content: newAlertOnlyImportant,
      });
      setNewAlertKeyword('');
      setNewAlertSubscribers([]);
      setNewAlertOnlyImportant(false);
      loadKeywordAlerts();
    } catch (e) {
      alert(`Failed to create alert: ${e.message}`);
    } finally {
      setKeywordAlertsLoading(false);
    }
  };

  const handleDeleteKeywordAlert = async (alertId) => {
    if (!window.confirm('Delete this keyword alert?')) return;
    try {
      await OrgManager.deleteKeywordAlert(activeOrg.id, alertId);
      loadKeywordAlerts();
    } catch (e) {
      alert(`Failed to delete alert: ${e.message}`);
    }
  };

  const handleToggleKeywordAlert = async (alertId, currentActive) => {
    try {
      await OrgManager.updateKeywordAlert(activeOrg.id, alertId, { is_active: !currentActive });
      loadKeywordAlerts();
    } catch (e) {
      alert(`Failed to toggle alert: ${e.message}`);
    }
  };

  const handleToggleAlertImportance = async (alertId, currentValue) => {
    try {
      await OrgManager.updateKeywordAlert(activeOrg.id, alertId, { only_important_content: !currentValue });
      loadKeywordAlerts();
    } catch (e) {
      alert(`Failed to update alert: ${e.message}`);
    }
  };

  const handleUpdateAlertSubscribers = async (alertId) => {
    try {
      await OrgManager.updateKeywordAlert(activeOrg.id, alertId, { subscriber_ids: editingAlertSubscribers });
      setEditingAlertId(null);
      setEditingAlertSubscribers([]);
      loadKeywordAlerts();
    } catch (e) {
      alert(`Failed to update subscribers: ${e.message}`);
    }
  };

  // ── TV Watch Terms ─────────────────────────────────────────────────────────
  const loadTvTerms = () => {
    if (!activeOrg) return;
    OrgManager.listTvWatchTerms(activeOrg.id)
      .then(r => { setTvTerms(r.terms || []); setTvLists(r.lists || []); })
      .catch(() => {});
  };
  useEffect(() => { loadTvTerms(); }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTvTerm = async () => {
    if (!newTvTerm.trim() || !newTvTermListId || !activeOrg) return;
    setTvTermsLoading(true);
    try {
      await OrgManager.createTvWatchTerm(activeOrg.id, {
        term: newTvTerm.trim(),
        category: newTvTermCategory,
        source_list_id: newTvTermListId,
      });
      setNewTvTerm('');
      loadTvTerms();
    } catch (e) {
      alert(`Failed to add term: ${e.message}`);
    } finally {
      setTvTermsLoading(false);
    }
  };

  const handleToggleTvTerm = async (term) => {
    try {
      await OrgManager.updateTvWatchTerm(activeOrg.id, term.id, { enabled: !term.enabled });
      loadTvTerms();
    } catch (e) {
      alert(`Failed to toggle term: ${e.message}`);
    }
  };

  const handleSaveTvTermEdit = async (term) => {
    if (!editingTvTermText.trim()) return;
    try {
      await OrgManager.updateTvWatchTerm(activeOrg.id, term.id, { term: editingTvTermText.trim() });
      setEditingTvTermId(null);
      setEditingTvTermText('');
      loadTvTerms();
    } catch (e) {
      alert(`Failed to edit term: ${e.message}`);
    }
  };

  const handleChangeTvTermCategory = async (term, category) => {
    try {
      await OrgManager.updateTvWatchTerm(activeOrg.id, term.id, { category });
      loadTvTerms();
    } catch (e) {
      alert(`Failed to update category: ${e.message}`);
    }
  };

  const handleDeleteTvTerm = async (termId) => {
    if (!window.confirm('Remove this TV watch term?')) return;
    try {
      await OrgManager.deleteTvWatchTerm(activeOrg.id, termId);
      loadTvTerms();
    } catch (e) {
      alert(`Failed to delete term: ${e.message}`);
    }
  };

  const toggleTvSelected = (id) =>
    setTvSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const exitTvSelectMode = () => { setTvSelectMode(false); setTvSelected([]); };

  const handleBulkDeleteTv = async () => {
    if (tvSelected.length === 0) return;
    if (!window.confirm(`Delete ${tvSelected.length} selected term${tvSelected.length === 1 ? '' : 's'}?`)) return;
    try {
      await OrgManager.bulkDeleteTvWatchTerms(activeOrg.id, tvSelected);
      exitTvSelectMode();
      loadTvTerms();
    } catch (e) {
      alert(`Failed to delete terms: ${e.message}`);
    }
  };

  const saveEscalationConfig = async () => {
    if (!canEdit || !activeOrg) return;
    setEscalationSaving(true);
    setEscalationMsg('');
    try {
      await OrgManager.updateEscalationConfig(activeOrg.id, escalationConfig);
      setEscalationMsg('Saved');
      setTimeout(() => setEscalationMsg(''), 3000);
    } catch (e) {
      setEscalationMsg(`Error: ${e.message}`);
    } finally {
      setEscalationSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !activeOrg) return;
    setInviting(true);
    setInviteMsg('');
    try {
      await OrgManager.sendInvitation(activeOrg.id, inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      setInviteMsg(t('app.org.invitationSent'));
      loadMembers();
    } catch (e) {
      setInviteMsg(`Error: ${e.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (listenerId, role) => {
    try {
      await OrgManager.changeMemberRole(activeOrg.id, listenerId, role);
      loadMembers();
    } catch (e) {
      alert(`Failed to change role: ${e.message}`);
    }
  };

  const handleRemove = async (listenerId, username) => {
    if (!window.confirm(`Remove @${username} from this org?`)) return;
    try {
      await OrgManager.removeMember(activeOrg.id, listenerId);
      loadMembers();
    } catch (e) {
      alert(`Failed to remove member: ${e.message}`);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await OrgManager.revokeInvitation(activeOrg.id, inviteId);
      loadMembers();
    } catch (e) {
      alert(`Failed to revoke invitation: ${e.message}`);
    }
  };

  const handleReportFrequencyChange = async (listenerId, frequency) => {
    try {
      await OrgManager.updateMemberReportFrequency(activeOrg.id, listenerId, frequency);
      loadMembers();
    } catch (e) {
      alert(`Failed to update report frequency: ${e.message}`);
    }
  };

  const [advisorOpen, setAdvisorOpen] = useState(false);

  const EMPTY_ORG_PROFILE = {
    country:     null,
    execution:   { preferred_mode: null, can_post: false, can_dm: false, can_ads: false },
    team:        { team_size: null, response_speed: null },
    influencers: { has_network: false, niches: '', tiers: [], budget_tier: null, activation_speed: null },
    pr:          { has_pr_team: false, has_spokesperson: false, crisis_protocol: null },
    brand:       { sensitive_topics: '', competitors: '', target_audiences: '' },
    resources:   { ad_budget_tier: null, can_offer_products: false, has_content_studio: false },
  };

  const parseOrgProfile = (raw) => {
    if (!raw) return EMPTY_ORG_PROFILE;
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      country:     p.country || null,
      execution:   { ...EMPTY_ORG_PROFILE.execution,   ...p.execution },
      team:        { ...EMPTY_ORG_PROFILE.team,        ...p.team },
      influencers: {
        ...EMPTY_ORG_PROFILE.influencers,
        ...p.influencers,
        niches: Array.isArray(p.influencers?.niches) ? p.influencers.niches.join(', ') : (p.influencers?.niches || ''),
        tiers: p.influencers?.tiers || [],
      },
      pr:          { ...EMPTY_ORG_PROFILE.pr,          ...p.pr },
      brand:       {
        ...EMPTY_ORG_PROFILE.brand,
        ...p.brand,
        sensitive_topics: Array.isArray(p.brand?.sensitive_topics) ? p.brand.sensitive_topics.join(', ') : (p.brand?.sensitive_topics || ''),
        competitors: Array.isArray(p.brand?.competitors) ? p.brand.competitors.join(', ') : (p.brand?.competitors || ''),
        target_audiences: Array.isArray(p.brand?.target_audiences) ? p.brand.target_audiences.join(', ') : (p.brand?.target_audiences || ''),
      },
      resources:   { ...EMPTY_ORG_PROFILE.resources,   ...p.resources },
    };
  };

  const serializeOrgProfile = (prof) => {
    const csvToArr = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
    const out = {
      country:     prof.country || null,
      execution:   { ...prof.execution },
      team:        { ...prof.team },
      influencers: { ...prof.influencers, niches: csvToArr(prof.influencers.niches) },
      pr:          { ...prof.pr },
      brand:       {
        ...prof.brand,
        sensitive_topics: csvToArr(prof.brand.sensitive_topics),
        competitors: csvToArr(prof.brand.competitors),
        target_audiences: csvToArr(prof.brand.target_audiences),
      },
      resources:   { ...prof.resources },
    };
    const isEmpty = JSON.stringify(out) === JSON.stringify({
      country:     null,
      execution:   { preferred_mode: null, can_post: false, can_dm: false, can_ads: false },
      team:        { team_size: null, response_speed: null },
      influencers: { has_network: false, niches: [], tiers: [], budget_tier: null, activation_speed: null },
      pr:          { has_pr_team: false, has_spokesperson: false, crisis_protocol: null },
      brand:       { sensitive_topics: [], competitors: [], target_audiences: [] },
      resources:   { ad_budget_tier: null, can_offer_products: false, has_content_studio: false },
    });
    return isEmpty ? null : out;
  };

  useEffect(() => {
    if (!activeOrg) return;
    OrgManager.getOrg(activeOrg.id)
      .then(data => {
        setForm({
          name:                     data.name || '',
          logo_url:                 data.logo_url || '',
          default_writing_style:    data.default_writing_style || '',
          default_reaction_context: data.default_reaction_context || '',
          default_language:         data.default_language || 'en',
          brand_scale:              data.brand_scale || 'national',
          min_role_for_org_posting: data.min_role_for_org_posting || 'member',
          org_profile:              parseOrgProfile(data.org_profile),
        });
        if (data.settings?.escalation_config) {
          setEscalationConfig(prev => ({ ...prev, ...data.settings.escalation_config }));
        }
      })
      .catch(e => console.error('OrgSettingsScreen load error:', e));
  }, [activeOrg]);

  const save = async () => {
    if (!canEdit || !activeOrg) return;
    setSaving(true);
    setMessage('');
    try {
      const payload = { ...form, org_profile: serializeOrgProfile(form.org_profile) };
      await OrgManager.updateOrg(activeOrg.id, payload);
      await refreshOrgs();
      setMessage(t('app.org.savedSuccessfully'));
    } catch (e) {
      setMessage(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (v) => setForm(f => ({ ...f, [key]: v }));
  const setProf = (section, field) => (v) =>
    setForm(f => ({ ...f, org_profile: { ...f.org_profile, [section]: { ...f.org_profile[section], [field]: v } } }));

  const selectedLang = LANGUAGES.find(l => l.code === form.default_language);
  const filteredLangs = langSearch
    ? LANGUAGES.filter(l => l.name.toLowerCase().includes(langSearch.toLowerCase()))
    : LANGUAGES;

  if (!activeOrg) return (
    <View style={styles.center}>
      <Text style={{ color: theme.textSecondary }}>{t('app.org.noActiveOrg')}</Text>
    </View>
  );

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.bg }]}
      contentContainerStyle={styles.content}
    >
      <Text style={[styles.title, { color: theme.textPrimary }]}>{t('app.org.settingsTitle')}</Text>
      {!canEdit && (
        <View style={[styles.readonlyBanner, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}>
          <Text style={{ color: theme.textSecondary, fontSize: 13 }}>
            {t('app.org.readonlyBanner')}
          </Text>
        </View>
      )}

      {/* Name */}
      <Field label={t('app.org.orgName')} theme={theme}>
        <TextInput
          style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
          value={form.name} onChangeText={set('name')} editable={canEdit}
          placeholderTextColor={theme.inputPlaceholder}
        />
      </Field>

      {canEdit && (<>
      {/* Logo URL */}
      <Field label={t('app.org.logoUrl')} theme={theme}>
        <View style={styles.logoRow}>
          {form.logo_url ? (
            <Image source={{ uri: form.logo_url }} style={styles.logoPreview} />
          ) : (
            <View style={[styles.logoPreview, styles.logoPlaceholder, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}>
              <Text style={{ color: theme.textMuted, fontSize: 10 }}>{t('app.org.noLogo')}</Text>
            </View>
          )}
          <TextInput
            style={[styles.input, styles.flex1, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
            value={form.logo_url} onChangeText={set('logo_url')} editable={canEdit}
            placeholder="https://example.com/logo.png"
            placeholderTextColor={theme.inputPlaceholder}
            autoCapitalize="none" autoCorrect={false}
          />
        </View>
      </Field>

      {/* Default Language — searchable dropdown */}
      <Field label={t('app.org.defaultLanguage')} theme={theme}>
        <TouchableOpacity
          disabled={!canEdit}
          style={[styles.select, { backgroundColor: theme.inputBg, borderColor: theme.border }, !canEdit && styles.disabled]}
          onPress={() => { setLangOpen(v => !v); setLangSearch(''); }}
        >
          <Text style={{ color: selectedLang ? theme.textPrimary : theme.inputPlaceholder, fontSize: 14, flex: 1 }}>
            {selectedLang ? `${selectedLang.name} (${selectedLang.code})` : t('app.org.selectLanguage')}
          </Text>
          <FaChevronDown size={12} color={theme.textSecondary} style={{ transform: langOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </TouchableOpacity>
        {langOpen && (
          <View style={[styles.langDropdown, { backgroundColor: theme.bgCard ?? theme.bgElevated, borderColor: theme.border }]}>
            <View style={[styles.langSearchWrap, { borderBottomColor: theme.border }]}>
              <TextInput
                style={[styles.langSearch, { color: theme.textPrimary, backgroundColor: 'transparent' }]}
                value={langSearch} onChangeText={setLangSearch}
                placeholder={t('app.org.searchLanguages')} placeholderTextColor={theme.inputPlaceholder}
                autoFocus
              />
              {langSearch ? (
                <TouchableOpacity onPress={() => setLangSearch('')}>
                  <FaTimes size={12} color={theme.textMuted} />
                </TouchableOpacity>
              ) : null}
            </View>
            <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
              {filteredLangs.map(l => (
                <TouchableOpacity
                  key={l.code}
                  style={[styles.langOption, l.code === form.default_language && { backgroundColor: '#0066cc14' }]}
                  onPress={() => { set('default_language')(l.code); setLangOpen(false); setLangSearch(''); }}
                >
                  <Text style={{ color: theme.textPrimary, fontSize: 13, flex: 1 }}>{l.name}</Text>
                  <Text style={{ color: theme.textMuted, fontSize: 11, marginRight: 8 }}>{l.code}</Text>
                  {l.code === form.default_language && <FaCheck size={11} color="#0066cc" />}
                </TouchableOpacity>
              ))}
              {filteredLangs.length === 0 && (
                <Text style={{ color: theme.textMuted, padding: 12, fontSize: 13 }}>{t('app.org.noLanguagesFound')}</Text>
              )}
            </ScrollView>
          </View>
        )}
      </Field>

      {/* Brand Scale */}
      <Field label={t('app.org.brandScale')} theme={theme}>
        <View style={styles.scaleRow}>
          {BRAND_SCALES.map(s => {
            const active = form.brand_scale === s.value;
            return (
              <TouchableOpacity
                key={s.value}
                disabled={!canEdit}
                onPress={() => set('brand_scale')(s.value)}
                style={[styles.scaleChip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}
              >
                <Text style={{ color: active ? '#fff' : theme.textPrimary, fontWeight: '600', fontSize: 13 }}>{t(s.labelKey)}</Text>
                <Text style={{ color: active ? 'rgba(255,255,255,0.75)' : theme.textMuted, fontSize: 11, marginTop: 2 }}>{t(s.descKey)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </Field>

      {/* Default Writing Style */}
      <Field label={t('app.org.defaultWritingStyle')} theme={theme} hint={t('app.org.defaultWritingStyleHint')}>
        <TextInput
          style={[styles.input, styles.textarea, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
          value={form.default_writing_style} onChangeText={set('default_writing_style')}
          editable={canEdit} multiline
          placeholder={t('app.org.writingStylePlaceholder')}
          placeholderTextColor={theme.inputPlaceholder}
        />
      </Field>

      {/* Default Reaction Context */}
      <Field label={t('app.org.defaultReactionContext')} theme={theme} hint={t('app.org.defaultReactionContextHint')}>
        <TextInput
          style={[styles.input, styles.textarea, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
          value={form.default_reaction_context} onChangeText={set('default_reaction_context')}
          editable={canEdit} multiline
          placeholder="e.g. We are a government communications office focused on…"
          placeholderTextColor={theme.inputPlaceholder}
        />
      </Field>
      </>)}

      {/* AI Advisor Context — collapsible */}
      {canEdit && form.org_profile && (
        <View style={[styles.fieldWrap]}>
          <TouchableOpacity
            onPress={() => setAdvisorOpen(v => !v)}
            style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
          >
            <View style={{ flex: 1 }}>
              <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>{t('app.org.aiAdvisorContext')}</Text>
              <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>{t('app.org.aiAdvisorContextHint')}</Text>
            </View>
            {advisorOpen
              ? <FaChevronDown size={12} color={theme.textSecondary} />
              : <FaChevronRight size={12} color={theme.textSecondary} />
            }
          </TouchableOpacity>

          {advisorOpen && (
            <View style={[styles.advisorBody, { borderColor: theme.border }]}>
              {/* Country / Region */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary }]}>{t('app.org.country')}</Text>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.countryHint')}</Text>
              <TouchableOpacity
                disabled={!canEdit}
                style={[styles.select, { backgroundColor: theme.inputBg, borderColor: theme.border, marginBottom: 4 }, !canEdit && styles.disabled]}
                onPress={() => { setCountryOpen(v => !v); setCountrySearch(''); }}
              >
                <Text style={{ color: form.org_profile.country ? theme.textPrimary : theme.inputPlaceholder, fontSize: 14, flex: 1 }}>
                  {form.org_profile.country || t('app.org.selectCountry')}
                </Text>
                <FaChevronDown size={12} color={theme.textSecondary} style={{ transform: countryOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </TouchableOpacity>
              {countryOpen && (
                <View style={[styles.langDropdown, { backgroundColor: theme.bgCard ?? theme.bgElevated, borderColor: theme.border, marginBottom: 8 }]}>
                  <View style={[styles.langSearchWrap, { borderBottomColor: theme.border }]}>
                    <TextInput
                      style={[styles.langSearch, { color: theme.textPrimary, backgroundColor: 'transparent' }]}
                      value={countrySearch} onChangeText={setCountrySearch}
                      placeholder={t('app.org.searchCountries')} placeholderTextColor={theme.inputPlaceholder}
                      autoFocus
                    />
                    {countrySearch ? (
                      <TouchableOpacity onPress={() => setCountrySearch('')}>
                        <FaTimes size={12} color={theme.textMuted} />
                      </TouchableOpacity>
                    ) : null}
                  </View>
                  <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
                    {COUNTRIES
                      .filter(c => !countrySearch || c.toLowerCase().includes(countrySearch.toLowerCase()))
                      .map(c => (
                        <TouchableOpacity
                          key={c}
                          style={[styles.langOption, c === form.org_profile.country && { backgroundColor: '#0066cc14' }]}
                          onPress={() => {
                            setForm(f => ({ ...f, org_profile: { ...f.org_profile, country: c } }));
                            setCountryOpen(false);
                            setCountrySearch('');
                          }}
                        >
                          <Text style={{ color: theme.textPrimary, fontSize: 13, flex: 1 }}>{c}</Text>
                          {c === form.org_profile.country && <FaCheck size={11} color="#0066cc" />}
                        </TouchableOpacity>
                      ))
                    }
                    {COUNTRIES.filter(c => !countrySearch || c.toLowerCase().includes(countrySearch.toLowerCase())).length === 0 && (
                      <Text style={{ color: theme.textMuted, padding: 12, fontSize: 13 }}>{t('app.org.noCountriesFound')}</Text>
                    )}
                  </ScrollView>
                </View>
              )}

              {/* Execution Channels */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.executionChannels')}</Text>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.preferredMode')}</Text>
              <View style={styles.chipRow}>
                {[{ v: 'direct', k: 'modeDirect' }, { v: 'influencer', k: 'modeInfluencer' }, { v: 'hybrid', k: 'modeHybrid' }].map(m => {
                  const active = form.org_profile.execution.preferred_mode === m.v;
                  return (
                    <TouchableOpacity key={m.v} disabled={!canEdit} onPress={() => setProf('execution', 'preferred_mode')(active ? null : m.v)}
                      style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                      <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${m.k}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={[styles.toggleRow, { marginTop: 10 }]}>
                <ToggleField label={t('app.org.canPostDirectly')} value={form.org_profile.execution.can_post} onChange={setProf('execution', 'can_post')} canEdit={canEdit} theme={theme} />
                <ToggleField label={t('app.org.canDmUsers')} value={form.org_profile.execution.can_dm} onChange={setProf('execution', 'can_dm')} canEdit={canEdit} theme={theme} />
                <ToggleField label={t('app.org.canRunPaidAds')} value={form.org_profile.execution.can_ads} onChange={setProf('execution', 'can_ads')} canEdit={canEdit} theme={theme} />
              </View>

              {/* Team */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.teamSection')}</Text>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.teamSize')}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, width: 100 }, !canEdit && styles.disabled]}
                value={form.org_profile.team.team_size != null ? String(form.org_profile.team.team_size) : ''}
                onChangeText={v => setProf('team', 'team_size')(v ? parseInt(v, 10) || null : null)}
                editable={canEdit} keyboardType="numeric" placeholder="#"
                placeholderTextColor={theme.inputPlaceholder}
              />
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary, marginTop: 10 }]}>{t('app.org.responseSpeed')}</Text>
              <View style={styles.chipRow}>
                {[{ v: 'fast', k: 'speedFast' }, { v: 'standard', k: 'speedStandard' }, { v: 'slow', k: 'speedSlow' }].map(s => {
                  const active = form.org_profile.team.response_speed === s.v;
                  return (
                    <TouchableOpacity key={s.v} disabled={!canEdit} onPress={() => setProf('team', 'response_speed')(active ? null : s.v)}
                      style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                      <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${s.k}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {/* Influencer Network */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.influencerNetwork')}</Text>
              <ToggleField label={t('app.org.hasInfluencerNetwork')} value={form.org_profile.influencers.has_network} onChange={setProf('influencers', 'has_network')} canEdit={canEdit} theme={theme} />
              {form.org_profile.influencers.has_network && (
                <View style={{ marginTop: 8, gap: 10 }}>
                  <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.influencerNiches')}</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
                    value={form.org_profile.influencers.niches} onChangeText={setProf('influencers', 'niches')}
                    editable={canEdit} placeholder={t('app.org.nichesPlaceholder')} placeholderTextColor={theme.inputPlaceholder}
                  />
                  <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.influencerTiers')}</Text>
                  <View style={styles.chipRow}>
                    {[{ v: 'nano', k: 'tierNano' }, { v: 'micro', k: 'tierMicro' }, { v: 'mid', k: 'tierMid' }, { v: 'macro', k: 'tierMacro' }].map(tier => {
                      const active = (form.org_profile.influencers.tiers || []).includes(tier.v);
                      return (
                        <TouchableOpacity key={tier.v} disabled={!canEdit} onPress={() => {
                          const cur = form.org_profile.influencers.tiers || [];
                          setProf('influencers', 'tiers')(active ? cur.filter(t2 => t2 !== tier.v) : [...cur, tier.v]);
                        }}
                          style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                          <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${tier.k}`)}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                  <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.influencerBudget')}</Text>
                  <View style={styles.chipRow}>
                    {[{ v: 'none', k: 'budgetNone' }, { v: 'low', k: 'budgetLow' }, { v: 'moderate', k: 'budgetModerate' }, { v: 'high', k: 'budgetHigh' }].map(b => {
                      const active = form.org_profile.influencers.budget_tier === b.v;
                      return (
                        <TouchableOpacity key={b.v} disabled={!canEdit} onPress={() => setProf('influencers', 'budget_tier')(active ? null : b.v)}
                          style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                          <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${b.k}`)}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                  <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.activationSpeed')}</Text>
                  <View style={styles.chipRow}>
                    {[{ v: 'hours', k: 'speedHours' }, { v: 'days', k: 'speedDays' }, { v: 'weeks', k: 'speedWeeks' }].map(s => {
                      const active = form.org_profile.influencers.activation_speed === s.v;
                      return (
                        <TouchableOpacity key={s.v} disabled={!canEdit} onPress={() => setProf('influencers', 'activation_speed')(active ? null : s.v)}
                          style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                          <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${s.k}`)}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              )}

              {/* PR & Media */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.prSection')}</Text>
              <View style={styles.toggleRow}>
                <ToggleField label={t('app.org.hasPrTeam')} value={form.org_profile.pr.has_pr_team} onChange={setProf('pr', 'has_pr_team')} canEdit={canEdit} theme={theme} />
                <ToggleField label={t('app.org.hasSpokesperson')} value={form.org_profile.pr.has_spokesperson} onChange={setProf('pr', 'has_spokesperson')} canEdit={canEdit} theme={theme} />
              </View>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary, marginTop: 10 }]}>{t('app.org.crisisProtocol')}</Text>
              <View style={styles.chipRow}>
                {[{ v: 'none', k: 'protocolNone' }, { v: 'basic', k: 'protocolBasic' }, { v: 'formal', k: 'protocolFormal' }].map(c => {
                  const active = form.org_profile.pr.crisis_protocol === c.v;
                  return (
                    <TouchableOpacity key={c.v} disabled={!canEdit} onPress={() => setProf('pr', 'crisis_protocol')(active ? null : c.v)}
                      style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                      <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${c.k}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {/* Brand */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.brandSection')}</Text>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.sensitiveTopics')}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
                value={form.org_profile.brand.sensitive_topics} onChangeText={setProf('brand', 'sensitive_topics')}
                editable={canEdit} placeholder={t('app.org.sensitiveTopicsPlaceholder')} placeholderTextColor={theme.inputPlaceholder}
              />
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary, marginTop: 10 }]}>{t('app.org.targetAudiences')}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border }, !canEdit && styles.disabled]}
                value={form.org_profile.brand.target_audiences} onChangeText={setProf('brand', 'target_audiences')}
                editable={canEdit} placeholder={t('app.org.targetAudiencesPlaceholder')} placeholderTextColor={theme.inputPlaceholder}
              />

              {/* Resources */}
              <Text style={[styles.advisorSectionTitle, { color: theme.textPrimary, marginTop: 18 }]}>{t('app.org.resourcesSection')}</Text>
              <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>{t('app.org.adBudgetTier')}</Text>
              <View style={styles.chipRow}>
                {[{ v: 'none', k: 'budgetNone' }, { v: 'low', k: 'budgetLow' }, { v: 'moderate', k: 'budgetModerate' }, { v: 'high', k: 'budgetHigh' }].map(b => {
                  const active = form.org_profile.resources.ad_budget_tier === b.v;
                  return (
                    <TouchableOpacity key={b.v} disabled={!canEdit} onPress={() => setProf('resources', 'ad_budget_tier')(active ? null : b.v)}
                      style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                      <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{t(`app.org.${b.k}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={[styles.toggleRow, { marginTop: 10 }]}>
                <ToggleField label={t('app.org.canOfferProducts')} value={form.org_profile.resources.can_offer_products} onChange={setProf('resources', 'can_offer_products')} canEdit={canEdit} theme={theme} />
                <ToggleField label={t('app.org.hasContentStudio')} value={form.org_profile.resources.has_content_studio} onChange={setProf('resources', 'has_content_studio')} canEdit={canEdit} theme={theme} />
              </View>
            </View>
          )}
        </View>
      )}

      {canEdit && (<>
      {/* Min role for org X posting */}
      <Field label={t('app.org.minRolePosting')} theme={theme} hint={t('app.org.minRolePostingHint')}>
        <View style={styles.chipRow}>
          {ROLES.map(r => {
            const active = form.min_role_for_org_posting === r.value;
            return (
              <TouchableOpacity
                key={r.value}
                disabled={!canEdit}
                onPress={() => set('min_role_for_org_posting')(r.value)}
                style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}
              >
                <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>
                  {t(r.labelKey)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </Field>

      {message ? (
        <View style={[styles.messageBanner, { backgroundColor: message.startsWith('Error') ? '#ef444420' : '#22c55e20', borderColor: message.startsWith('Error') ? '#ef4444' : '#22c55e' }]}>
          <Text style={{ color: message.startsWith('Error') ? '#ef4444' : '#22c55e', fontSize: 13, fontWeight: '500' }}>{message}</Text>
        </View>
      ) : null}

      {canEdit && (
        <TouchableOpacity style={[styles.saveBtn, { backgroundColor: '#0066cc' }, saving && { opacity: 0.7 }]} onPress={save} disabled={saving}>
          <Text style={styles.saveBtnText}>{saving ? t('app.org.saving') : t('app.org.saveChanges')}</Text>
        </TouchableOpacity>
      )}
      </>)}

      {/* Tags */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={[styles.fieldWrap]}>
        <TouchableOpacity
          onPress={() => setTagsOpen(v => !v)}
          style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>Tags</Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>Categorize posts into org-defined tags automatically</Text>
          </View>
          {tagsOpen
            ? <FaChevronDown size={12} color={theme.textSecondary} />
            : <FaChevronRight size={12} color={theme.textSecondary} />
          }
        </TouchableOpacity>

        {tagsOpen && (
          <View style={[styles.advisorBody, { borderColor: theme.border }]}>
            {tags.length > 0 && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {tags.map(tag => (
                  <View key={tag.id} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: theme.bgElevated, borderWidth: 1, borderColor: theme.border, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6, gap: 6 }}>
                    <Text style={{ color: theme.textPrimary, fontSize: 13, fontWeight: '500' }}>{tag.name}</Text>
                    {canEdit && (
                      <TouchableOpacity onPress={() => handleDeleteTag(tag.id)} style={{ padding: 2 }}>
                        <FaTimes size={10} color="#ef4444" />
                      </TouchableOpacity>
                    )}
                  </View>
                ))}
              </View>
            )}
            {tags.length === 0 && (
              <Text style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12 }}>No tags defined yet</Text>
            )}
            {canEdit && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, flex: 1 }]}
                  value={newTagName}
                  onChangeText={setNewTagName}
                  placeholder="New tag name..."
                  placeholderTextColor={theme.inputPlaceholder}
                  onSubmitEditing={handleAddTag}
                />
                <TouchableOpacity
                  onPress={handleAddTag}
                  disabled={tagsLoading || !newTagName.trim()}
                  style={{ backgroundColor: '#0066cc', paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, opacity: tagsLoading || !newTagName.trim() ? 0.5 : 1 }}
                >
                  {tagsLoading
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Add</Text>
                  }
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Keyword Alerts */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={[styles.fieldWrap]}>
        <TouchableOpacity
          onPress={() => setKeywordAlertsOpen(v => !v)}
          style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>Keyword Alerts</Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>Get email alerts when specific keywords appear in monitored posts</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {keywordAlerts.length > 0 && (
              <View style={{ backgroundColor: '#f59e0b22', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2 }}>
                <Text style={{ color: '#f59e0b', fontSize: 12, fontWeight: '700' }}>{keywordAlerts.length}</Text>
              </View>
            )}
            {keywordAlertsOpen
              ? <FaChevronDown size={12} color={theme.textSecondary} />
              : <FaChevronRight size={12} color={theme.textSecondary} />
            }
          </View>
        </TouchableOpacity>

        {keywordAlertsOpen && (
          <View style={[styles.advisorBody, { borderColor: theme.border }]}>
            {keywordAlerts.length > 0 && (
              <View style={{ gap: 10, marginBottom: 16 }}>
                {keywordAlerts.map(alert => (
                  <View key={alert.id} style={{ backgroundColor: theme.bgElevated, borderWidth: 1, borderColor: theme.border, borderRadius: 12, padding: 14 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>"{alert.keyword}"</Text>
                          {!alert.is_active && (
                            <View style={{ backgroundColor: '#6b728014', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                              <Text style={{ color: '#6b7280', fontSize: 10, fontWeight: '600' }}>PAUSED</Text>
                            </View>
                          )}
                        </View>
                        <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 4 }}>
                          {alert.subscribers.length} recipient{alert.subscribers.length !== 1 ? 's' : ''}: {alert.subscribers.map(s => s.username || s.email).join(', ')}
                        </Text>
                      </View>
                      {canEdit && (
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                          <Switch
                            value={alert.is_active}
                            onValueChange={() => handleToggleKeywordAlert(alert.id, alert.is_active)}
                            trackColor={{ false: theme.border, true: '#f59e0b88' }}
                            thumbColor={alert.is_active ? '#f59e0b' : '#ccc'}
                            style={{ transform: [{ scale: 0.8 }] }}
                          />
                          <TouchableOpacity
                            onPress={() => {
                              if (editingAlertId === alert.id) {
                                setEditingAlertId(null);
                              } else {
                                setEditingAlertId(alert.id);
                                setEditingAlertSubscribers(alert.subscribers.map(s => s.listener_id));
                              }
                            }}
                            style={{ padding: 4 }}
                          >
                            <Text style={{ color: '#0066cc', fontSize: 12, fontWeight: '600' }}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => handleDeleteKeywordAlert(alert.id)} style={{ padding: 4 }}>
                            <FaTrash size={11} color="#ef4444" />
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>

                    {/* Only important content toggle */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: theme.border }}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ color: theme.textPrimary, fontSize: 12, fontWeight: '600' }}>Only important content</Text>
                        <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 2 }}>AI filters out routine mentions — alert only on major news, high-reach, or hostile content</Text>
                      </View>
                      {canEdit ? (
                        <Switch
                          value={!!alert.only_important_content}
                          onValueChange={() => handleToggleAlertImportance(alert.id, alert.only_important_content)}
                          trackColor={{ false: theme.border, true: '#f59e0b88' }}
                          thumbColor={alert.only_important_content ? '#f59e0b' : '#ccc'}
                          style={{ transform: [{ scale: 0.8 }] }}
                        />
                      ) : (
                        alert.only_important_content && (
                          <View style={{ backgroundColor: '#f59e0b22', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                            <Text style={{ color: '#f59e0b', fontSize: 10, fontWeight: '600' }}>IMPORTANT ONLY</Text>
                          </View>
                        )
                      )}
                    </View>

                    {/* Edit subscribers inline */}
                    {editingAlertId === alert.id && canEdit && (
                      <View style={{ marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme.border }}>
                        <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', marginBottom: 8 }}>Recipients</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                          {members.map(m => {
                            const selected = editingAlertSubscribers.includes(m.listener_id);
                            return (
                              <TouchableOpacity
                                key={m.listener_id}
                                onPress={() => {
                                  setEditingAlertSubscribers(prev =>
                                    selected ? prev.filter(id => id !== m.listener_id) : [...prev, m.listener_id]
                                  );
                                }}
                                style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: selected ? '#f59e0b' : theme.border, backgroundColor: selected ? '#f59e0b18' : 'transparent' }}
                              >
                                <Text style={{ color: selected ? '#f59e0b' : theme.textSecondary, fontSize: 12, fontWeight: selected ? '600' : '400' }}>@{m.username}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
                          <TouchableOpacity
                            onPress={() => handleUpdateAlertSubscribers(alert.id)}
                            disabled={editingAlertSubscribers.length === 0}
                            style={{ backgroundColor: '#f59e0b', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, opacity: editingAlertSubscribers.length === 0 ? 0.5 : 1 }}
                          >
                            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Save</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => setEditingAlertId(null)}
                            style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}
                          >
                            <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            )}
            {keywordAlerts.length === 0 && (
              <Text style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12 }}>No keyword alerts defined yet</Text>
            )}

            {/* Add new alert form */}
            {canEdit && (
              <View style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border, borderRadius: 10, padding: 14, gap: 12 }}>
                <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 13 }}>New Alert</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                  value={newAlertKeyword}
                  onChangeText={setNewAlertKeyword}
                  placeholder="Keyword or phrase to monitor..."
                  placeholderTextColor={theme.inputPlaceholder}
                />
                <View>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', marginBottom: 6 }}>Who should receive alerts?</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {members.map(m => {
                      const selected = newAlertSubscribers.includes(m.listener_id);
                      return (
                        <TouchableOpacity
                          key={m.listener_id}
                          onPress={() => {
                            setNewAlertSubscribers(prev =>
                              selected ? prev.filter(id => id !== m.listener_id) : [...prev, m.listener_id]
                            );
                          }}
                          style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: selected ? '#f59e0b' : theme.border, backgroundColor: selected ? '#f59e0b18' : 'transparent' }}
                        >
                          <Text style={{ color: selected ? '#f59e0b' : theme.textSecondary, fontSize: 12, fontWeight: selected ? '600' : '400' }}>@{m.username}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: theme.textPrimary, fontSize: 12, fontWeight: '600' }}>Only important content</Text>
                    <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 2 }}>AI filters out routine mentions — alert only on major news, high-reach, or hostile content</Text>
                  </View>
                  <Switch
                    value={newAlertOnlyImportant}
                    onValueChange={setNewAlertOnlyImportant}
                    trackColor={{ false: theme.border, true: '#f59e0b88' }}
                    thumbColor={newAlertOnlyImportant ? '#f59e0b' : '#ccc'}
                    style={{ transform: [{ scale: 0.8 }] }}
                  />
                </View>
                <TouchableOpacity
                  onPress={handleAddKeywordAlert}
                  disabled={keywordAlertsLoading || !newAlertKeyword.trim() || newAlertSubscribers.length === 0}
                  style={{ backgroundColor: '#f59e0b', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, alignSelf: 'flex-start', opacity: keywordAlertsLoading || !newAlertKeyword.trim() || newAlertSubscribers.length === 0 ? 0.5 : 1 }}
                >
                  {keywordAlertsLoading
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Create Alert</Text>
                  }
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>

      {/* TV Watch Terms */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={[styles.fieldWrap]}>
        <TouchableOpacity
          onPress={() => setTvTermsOpen(v => !v)}
          style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>📺 Live TV Watch Terms</Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>Brand/competitor terms the TV monitor listens for. A spoken hit routes to its list. Changes apply without restarting the monitor.</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {tvTerms.length > 0 && (
              <View style={{ backgroundColor: '#8b5cf622', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2 }}>
                <Text style={{ color: '#8b5cf6', fontSize: 12, fontWeight: '700' }}>{tvTerms.filter(t => t.enabled).length}/{tvTerms.length}</Text>
              </View>
            )}
            {tvTermsOpen
              ? <FaChevronDown size={12} color={theme.textSecondary} />
              : <FaChevronRight size={12} color={theme.textSecondary} />
            }
          </View>
        </TouchableOpacity>

        {tvTermsOpen && (
          <View style={[styles.advisorBody, { borderColor: theme.border }]}>
            {canEdit && tvTerms.length > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                {!tvSelectMode ? (
                  <TouchableOpacity onPress={() => setTvSelectMode(true)} style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}>
                    <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Select</Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TouchableOpacity onPress={() => setTvSelected(tvTerms.map(t => t.id))} style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}>
                      <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Select all ({tvTerms.length})</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={handleBulkDeleteTv}
                      disabled={tvSelected.length === 0}
                      style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, backgroundColor: '#ef4444', opacity: tvSelected.length === 0 ? 0.5 : 1 }}
                    >
                      <Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>Delete ({tvSelected.length})</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={exitTvSelectMode} style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}>
                      <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}
            {['competitor', 'brand', 'general'].map(cat => {
              const items = tvTerms.filter(t => t.category === cat);
              if (items.length === 0) return null;
              const catLabel = cat === 'competitor' ? 'Competitors' : cat === 'brand' ? 'Our brands' : 'Topics';
              return (
                <View key={cat} style={{ marginBottom: 14 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                    {catLabel} · {items.filter(t => t.enabled).length}/{items.length}
                  </Text>
                  <View style={{ gap: 8 }}>
                    {items.map(term => (
                      <View key={term.id} style={{ backgroundColor: tvSelectMode && tvSelected.includes(term.id) ? '#8b5cf610' : theme.bgElevated, borderWidth: 1, borderColor: tvSelectMode && tvSelected.includes(term.id) ? '#8b5cf6' : theme.border, borderRadius: 10, padding: 10 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                          {tvSelectMode && (
                            <TouchableOpacity
                              onPress={() => toggleTvSelected(term.id)}
                              style={{ width: 20, height: 20, borderRadius: 5, borderWidth: 2, borderColor: tvSelected.includes(term.id) ? '#8b5cf6' : theme.border, backgroundColor: tvSelected.includes(term.id) ? '#8b5cf6' : 'transparent', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {tvSelected.includes(term.id) && <FaCheck size={10} color="#fff" />}
                            </TouchableOpacity>
                          )}
                          {editingTvTermId === term.id ? (
                            <TextInput
                              style={[styles.input, { flex: 1, backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                              value={editingTvTermText}
                              onChangeText={setEditingTvTermText}
                              autoFocus
                              onSubmitEditing={() => handleSaveTvTermEdit(term)}
                            />
                          ) : tvSelectMode ? (
                            <TouchableOpacity style={{ flex: 1 }} onPress={() => toggleTvSelected(term.id)}>
                              <Text style={{ color: term.enabled ? theme.textPrimary : theme.textMuted, fontWeight: '600', fontSize: 14, textDecorationLine: term.enabled ? 'none' : 'line-through' }}>{term.term}</Text>
                              <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 2 }}>→ {term.source_list || '—'}</Text>
                            </TouchableOpacity>
                          ) : (
                            <View style={{ flex: 1 }}>
                              <Text style={{ color: term.enabled ? theme.textPrimary : theme.textMuted, fontWeight: '600', fontSize: 14, textDecorationLine: term.enabled ? 'none' : 'line-through' }}>{term.term}</Text>
                              <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 2 }}>→ {term.source_list || '—'}</Text>
                            </View>
                          )}
                          {canEdit && !tvSelectMode && (
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                              {editingTvTermId === term.id ? (
                                <>
                                  <TouchableOpacity onPress={() => handleSaveTvTermEdit(term)} style={{ padding: 4 }}>
                                    <Text style={{ color: '#8b5cf6', fontSize: 12, fontWeight: '700' }}>Save</Text>
                                  </TouchableOpacity>
                                  <TouchableOpacity onPress={() => { setEditingTvTermId(null); setEditingTvTermText(''); }} style={{ padding: 4 }}>
                                    <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                                  </TouchableOpacity>
                                </>
                              ) : (
                                <>
                                  <Switch
                                    value={term.enabled}
                                    onValueChange={() => handleToggleTvTerm(term)}
                                    trackColor={{ false: theme.border, true: '#8b5cf688' }}
                                    thumbColor={term.enabled ? '#8b5cf6' : '#ccc'}
                                    style={{ transform: [{ scale: 0.8 }] }}
                                  />
                                  <TouchableOpacity onPress={() => { setEditingTvTermId(term.id); setEditingTvTermText(term.term); }} style={{ padding: 4 }}>
                                    <Text style={{ color: '#0066cc', fontSize: 12, fontWeight: '600' }}>Edit</Text>
                                  </TouchableOpacity>
                                  <TouchableOpacity onPress={() => handleDeleteTvTerm(term.id)} style={{ padding: 4 }}>
                                    <FaTrash size={11} color="#ef4444" />
                                  </TouchableOpacity>
                                </>
                              )}
                            </View>
                          )}
                        </View>
                        {editingTvTermId === term.id && canEdit && (
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                            {['competitor', 'brand', 'general'].map(c => (
                              <TouchableOpacity
                                key={c}
                                onPress={() => handleChangeTvTermCategory(term, c)}
                                style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, borderWidth: 1, borderColor: term.category === c ? '#8b5cf6' : theme.border, backgroundColor: term.category === c ? '#8b5cf618' : 'transparent' }}
                              >
                                <Text style={{ color: term.category === c ? '#8b5cf6' : theme.textSecondary, fontSize: 11, fontWeight: term.category === c ? '600' : '400' }}>{c}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                </View>
              );
            })}

            {tvTerms.length === 0 && (
              <Text style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12 }}>No TV watch terms yet.</Text>
            )}

            {/* Add new term */}
            {canEdit && (
              <View style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border, borderRadius: 10, padding: 14, gap: 12 }}>
                <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 13 }}>New Term</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                  value={newTvTerm}
                  onChangeText={setNewTvTerm}
                  placeholder="Brand / name spoken on TV…"
                  placeholderTextColor={theme.inputPlaceholder}
                />
                <View>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', marginBottom: 6 }}>Category</Text>
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    {['competitor', 'brand', 'general'].map(c => (
                      <TouchableOpacity
                        key={c}
                        onPress={() => setNewTvTermCategory(c)}
                        style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: newTvTermCategory === c ? '#8b5cf6' : theme.border, backgroundColor: newTvTermCategory === c ? '#8b5cf618' : 'transparent' }}
                      >
                        <Text style={{ color: newTvTermCategory === c ? '#8b5cf6' : theme.textSecondary, fontSize: 12, fontWeight: newTvTermCategory === c ? '600' : '400' }}>{c}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <View>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', marginBottom: 6 }}>Route hits to list</Text>
                  {tvLists.length === 0 ? (
                    <Text style={{ color: theme.textMuted, fontSize: 12 }}>This org has no lists yet.</Text>
                  ) : (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {tvLists.map(l => {
                        const selected = newTvTermListId === l.id;
                        return (
                          <TouchableOpacity
                            key={l.id}
                            onPress={() => setNewTvTermListId(l.id)}
                            style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: selected ? '#8b5cf6' : theme.border, backgroundColor: selected ? '#8b5cf618' : 'transparent' }}
                          >
                            <Text style={{ color: selected ? '#8b5cf6' : theme.textSecondary, fontSize: 12, fontWeight: selected ? '600' : '400' }}>{l.name}</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </View>
                <TouchableOpacity
                  onPress={handleAddTvTerm}
                  disabled={tvTermsLoading || !newTvTerm.trim() || !newTvTermListId}
                  style={{ backgroundColor: '#8b5cf6', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, alignSelf: 'flex-start', opacity: tvTermsLoading || !newTvTerm.trim() || !newTvTermListId ? 0.5 : 1 }}
                >
                  {tvTermsLoading
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Add Term</Text>
                  }
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Competitors */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={[styles.fieldWrap]}>
        <TouchableOpacity
          onPress={() => setCompetitorsOpen(v => !v)}
          style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>{t('app.org.competitorsSection')}</Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>{t('app.org.competitorsSectionHint')}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {competitors.length > 0 && (
              <View style={{ backgroundColor: '#ff453a22', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2 }}>
                <Text style={{ color: '#ff453a', fontSize: 12, fontWeight: '700' }}>{competitors.length}</Text>
              </View>
            )}
            {competitorsOpen
              ? <FaChevronDown size={12} color={theme.textSecondary} />
              : <FaChevronRight size={12} color={theme.textSecondary} />
            }
          </View>
        </TouchableOpacity>

        {competitorsOpen && (
          <View style={[styles.advisorBody, { borderColor: theme.border }]}>
            {competitors.length > 0 && (
              <View style={{ gap: 10, marginBottom: 16 }}>
                {competitors.map(comp => (
                  <View key={comp.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: theme.bgElevated, borderWidth: 1, borderColor: theme.border, borderRadius: 12, padding: 12 }}>
                    {comp.logo_url ? (
                      <Image source={{ uri: comp.logo_url }} style={{ width: 36, height: 36, borderRadius: 8 }} />
                    ) : (
                      <View style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#ff453a14', justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ color: '#ff453a', fontWeight: '700', fontSize: 15 }}>{(comp.name || '?')[0].toUpperCase()}</Text>
                      </View>
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: theme.textPrimary, fontWeight: '600', fontSize: 14 }}>{comp.name}</Text>
                      {comp.description ? <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }} numberOfLines={1}>{comp.description}</Text> : null}
                      {comp.keywords && comp.keywords.length > 0 && (
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {comp.keywords.slice(0, 5).map((kw, i) => (
                            <View key={i} style={{ backgroundColor: '#ff453a10', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                              <Text style={{ color: '#ff453a', fontSize: 10, fontWeight: '500' }}>{kw}</Text>
                            </View>
                          ))}
                          {comp.keywords.length > 5 && (
                            <Text style={{ color: theme.textMuted, fontSize: 10, alignSelf: 'center' }}>+{comp.keywords.length - 5}</Text>
                          )}
                        </View>
                      )}
                    </View>
                    {canEdit && (
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity onPress={() => handleEditCompetitor(comp)} style={{ padding: 4 }}>
                          <Text style={{ color: '#0066cc', fontSize: 12, fontWeight: '600' }}>{t('app.org.edit')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => handleDeleteCompetitor(comp.id, comp.name)} style={{ padding: 4 }}>
                          <FaTrash size={11} color="#ef4444" />
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            )}
            {competitors.length === 0 && !competitorForm && (
              <Text style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12 }}>{t('app.org.noCompetitors')}</Text>
            )}

            {/* Add/Edit form */}
            {canEdit && competitorForm && (
              <View style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border, borderRadius: 10, padding: 14, marginBottom: 12, gap: 10 }}>
                <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 13 }}>
                  {editingCompetitorId ? t('app.org.editCompetitor') : t('app.org.addCompetitor')}
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                  value={competitorForm.name}
                  onChangeText={v => setCompetitorForm(f => ({ ...f, name: v }))}
                  placeholder={t('app.org.competitorNamePlaceholder')}
                  placeholderTextColor={theme.inputPlaceholder}
                />
                <TextInput
                  style={[styles.input, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                  value={competitorForm.logo_url}
                  onChangeText={v => setCompetitorForm(f => ({ ...f, logo_url: v }))}
                  placeholder={t('app.org.competitorLogoPlaceholder')}
                  placeholderTextColor={theme.inputPlaceholder}
                  autoCapitalize="none" autoCorrect={false}
                />
                <TextInput
                  style={[styles.input, styles.textarea, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                  value={competitorForm.description}
                  onChangeText={v => setCompetitorForm(f => ({ ...f, description: v }))}
                  placeholder={t('app.org.competitorDescPlaceholder')}
                  placeholderTextColor={theme.inputPlaceholder}
                  multiline
                />
                <View>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: '600', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.org.competitorKeywords')}</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.bg, color: theme.textPrimary, borderColor: theme.border }]}
                    value={competitorForm.keywords_text}
                    onChangeText={v => setCompetitorForm(f => ({ ...f, keywords_text: v }))}
                    placeholder={t('app.org.competitorKeywordsPlaceholder')}
                    placeholderTextColor={theme.inputPlaceholder}
                  />
                  <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 3 }}>{t('app.org.competitorKeywordsHint')}</Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                  <TouchableOpacity
                    onPress={handleSaveCompetitor}
                    disabled={competitorsLoading || !competitorForm.name?.trim()}
                    style={{ backgroundColor: '#0066cc', paddingHorizontal: 16, paddingVertical: 9, borderRadius: 8, opacity: competitorsLoading || !competitorForm.name?.trim() ? 0.5 : 1 }}
                  >
                    {competitorsLoading
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{editingCompetitorId ? t('app.org.saveChanges') : t('app.org.addCompetitor')}</Text>
                    }
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => { setCompetitorForm(null); setEditingCompetitorId(null); }}
                    style={{ paddingHorizontal: 16, paddingVertical: 9, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}
                  >
                    <Text style={{ color: theme.textSecondary, fontSize: 13, fontWeight: '600' }}>{t('app.org.cancel')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {canEdit && !competitorForm && (
              <TouchableOpacity
                onPress={() => setCompetitorForm({ name: '', logo_url: '', description: '', keywords_text: '' })}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8 }}
              >
                <Text style={{ color: '#0066cc', fontSize: 18, fontWeight: '400', marginTop: -2 }}>+</Text>
                <Text style={{ color: '#0066cc', fontSize: 13, fontWeight: '600' }}>{t('app.org.addCompetitor')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {canEdit && (<>
      {/* Escalation Config */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={[styles.fieldWrap]}>
        <TouchableOpacity
          onPress={() => setEscalationOpen(v => !v)}
          style={[styles.advisorHeader, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 14 }}>Escalation Thresholds</Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>Configure when signals auto-escalate to urgent queue</Text>
          </View>
          {escalationOpen
            ? <FaChevronDown size={12} color={theme.textSecondary} />
            : <FaChevronRight size={12} color={theme.textSecondary} />
          }
        </TouchableOpacity>

        {escalationOpen && (
          <View style={[styles.advisorBody, { borderColor: theme.border }]}>
            <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>Min followers for negative escalation</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, width: 140, marginBottom: 12 }, !canEdit && styles.disabled]}
              value={String(escalationConfig.min_followers_for_negative_escalation || '')}
              onChangeText={v => setEscalationConfig(c => ({ ...c, min_followers_for_negative_escalation: parseInt(v, 10) || 0 }))}
              editable={canEdit} keyboardType="numeric" placeholder="50000"
              placeholderTextColor={theme.inputPlaceholder}
            />

            <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>Min sentiment for negative escalation (-1 to 0)</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, width: 140, marginBottom: 12 }, !canEdit && styles.disabled]}
              value={String(escalationConfig.min_sentiment_for_negative_escalation || '')}
              onChangeText={v => setEscalationConfig(c => ({ ...c, min_sentiment_for_negative_escalation: parseFloat(v) || -0.5 }))}
              editable={canEdit} keyboardType="numeric" placeholder="-0.5"
              placeholderTextColor={theme.inputPlaceholder}
            />

            <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>Viral reach tiers (triggers escalation with acceleration)</Text>
            <View style={[styles.chipRow, { marginBottom: 12 }]}>
              {['small', 'medium', 'large', 'viral'].map(tier => {
                const active = (escalationConfig.viral_reach_tiers || []).includes(tier);
                return (
                  <TouchableOpacity key={tier} disabled={!canEdit} onPress={() => {
                    const cur = escalationConfig.viral_reach_tiers || [];
                    setEscalationConfig(c => ({ ...c, viral_reach_tiers: active ? cur.filter(t2 => t2 !== tier) : [...cur, tier] }));
                  }}
                    style={[styles.chip, { borderColor: active ? '#0066cc' : theme.border, backgroundColor: active ? '#0066cc' : theme.bgElevated }, !canEdit && styles.disabled]}>
                    <Text style={{ color: active ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: active ? '600' : '400' }}>{tier}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>Pattern cluster min count</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, width: 140, marginBottom: 12 }, !canEdit && styles.disabled]}
              value={String(escalationConfig.pattern_cluster_min_count || '')}
              onChangeText={v => setEscalationConfig(c => ({ ...c, pattern_cluster_min_count: parseInt(v, 10) || 5 }))}
              editable={canEdit} keyboardType="numeric" placeholder="5"
              placeholderTextColor={theme.inputPlaceholder}
            />

            <Text style={[styles.advisorFieldLabel, { color: theme.textSecondary }]}>Pattern cluster time window (hours)</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, width: 140, marginBottom: 16 }, !canEdit && styles.disabled]}
              value={String(escalationConfig.pattern_cluster_hours_window || '')}
              onChangeText={v => setEscalationConfig(c => ({ ...c, pattern_cluster_hours_window: parseInt(v, 10) || 12 }))}
              editable={canEdit} keyboardType="numeric" placeholder="12"
              placeholderTextColor={theme.inputPlaceholder}
            />

            {canEdit && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <TouchableOpacity
                  style={[styles.saveBtn, { backgroundColor: '#0066cc', paddingHorizontal: 20, paddingVertical: 10, marginTop: 0 }, escalationSaving && { opacity: 0.7 }]}
                  onPress={saveEscalationConfig} disabled={escalationSaving}
                >
                  <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{escalationSaving ? 'Saving...' : 'Save Thresholds'}</Text>
                </TouchableOpacity>
                {escalationMsg ? (
                  <Text style={{ color: escalationMsg.startsWith('Error') ? '#ef4444' : '#22c55e', fontSize: 12, fontWeight: '500' }}>{escalationMsg}</Text>
                ) : null}
              </View>
            )}
          </View>
        )}
      </View>

      {/* Team Members */}
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      </>)}

      {canEdit && (<>
      <View style={[styles.sectionDivider, { borderTopColor: theme.border }]} />
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <FaUsers size={16} color={theme.textSecondary} />
          <Text style={[styles.title, { fontSize: 16, marginBottom: 0, color: theme.textPrimary }]}>{t('app.org.teamMembers')}</Text>
          <View style={{ backgroundColor: '#0066cc22', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2 }}>
            <Text style={{ color: '#0066cc', fontSize: 12, fontWeight: '700' }}>{members.length}</Text>
          </View>
        </View>
      </View>

      {/* Invite form — admins/owners only */}
      {canManage && (
        <View style={[styles.inviteBox, { backgroundColor: theme.bgElevated, borderColor: theme.border }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <FaEnvelope size={13} color="#0066cc" />
            <Text style={{ color: theme.textPrimary, fontSize: 13, fontWeight: '700' }}>{t('app.org.inviteByEmail')}</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextInput
              style={[styles.input, styles.flex1, { backgroundColor: theme.inputBg, color: theme.textPrimary, borderColor: theme.border, minWidth: 180 }]}
              value={inviteEmail}
              onChangeText={setInviteEmail}
              placeholder={t('app.org.inviteEmailPlaceholder')}
              placeholderTextColor={theme.inputPlaceholder}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
            />
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {ROLES.filter(r => r.value !== 'owner').map(r => (
                <TouchableOpacity
                  key={r.value}
                  onPress={() => setInviteRole(r.value)}
                  style={[styles.chip, { borderColor: inviteRole === r.value ? '#0066cc' : theme.border, backgroundColor: inviteRole === r.value ? '#0066cc' : theme.bgElevated }]}
                >
                  <Text style={{ color: inviteRole === r.value ? '#fff' : theme.textSecondary, fontSize: 12, fontWeight: '600' }}>{t(r.labelKey)}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              onPress={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              style={[styles.inviteBtn, { opacity: inviting || !inviteEmail.trim() ? 0.5 : 1 }]}
            >
              {inviting
                ? <ActivityIndicator size="small" color="#fff" />
                : <><FaUserPlus size={12} color="#fff" /><Text style={{ color: '#fff', fontSize: 13, fontWeight: '700', marginLeft: 6 }}>{t('app.org.invite')}</Text></>
              }
            </TouchableOpacity>
          </View>
          {inviteMsg ? (
            <Text style={{ color: inviteMsg.startsWith('Error') ? '#ef4444' : '#22c55e', fontSize: 12, marginTop: 8 }}>{inviteMsg}</Text>
          ) : null}
        </View>
      )}

      {/* Pending invitations */}
      {invitations.filter(i => i.status === 'pending').length > 0 && (
        <View style={{ marginBottom: 16 }}>
          <Text style={{ color: theme.textMuted, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>{t('app.org.pendingInvitations')}</Text>
          <View style={{ gap: 6 }}>
            {invitations.filter(i => i.status === 'pending').map(inv => (
              <View key={inv.id} style={[styles.memberRow, { backgroundColor: theme.bgElevated, borderColor: theme.border, opacity: 0.8 }]}>
                <View style={[styles.memberAvatar, { backgroundColor: '#f59e0b22' }]}>
                  <FaEnvelope size={14} color="#f59e0b" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: theme.textPrimary, fontWeight: '600', fontSize: 13 }}>{inv.email}</Text>
                  <Text style={{ color: theme.textMuted, fontSize: 11 }}>{t('app.org.invitedRole', { role: inv.role })}</Text>
                </View>
                {canManage && (
                  <TouchableOpacity onPress={() => handleRevokeInvite(inv.id)} style={{ padding: 6 }}>
                    <FaTimes size={13} color="#ef4444" />
                  </TouchableOpacity>
                )}
              </View>
            ))}
          </View>
        </View>
      )}

      {membersLoading ? (
        <ActivityIndicator size="small" color="#0066cc" style={{ marginBottom: 24 }} />
      ) : members.length === 0 ? (
        <Text style={{ color: theme.textMuted, fontSize: 13, marginBottom: 24 }}>{t('app.org.noMembers')}</Text>
      ) : (
        <View style={{ marginBottom: 32, gap: 12 }}>
          {members.map(m => {
            const ROLE_COLORS = { owner: '#f59e0b', admin: '#8b5cf6', member: '#3b82f6', viewer: '#6b7280' };
            const roleColor = ROLE_COLORS[m.role] || '#6b7280';
            return (
              <View key={m.listener_id} style={{ backgroundColor: theme.bgElevated, borderWidth: 1, borderColor: theme.border, borderRadius: 14, padding: 18 }}>
                {/* Top row: avatar + info + role badge */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                  <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: roleColor + '18', justifyContent: 'center', alignItems: 'center' }}>
                    <Text style={{ color: roleColor, fontWeight: '700', fontSize: 17 }}>
                      {(m.username || '?')[0].toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: 15 }}>@{m.username || m.listener_id}</Text>
                    {m.email ? <Text style={{ color: theme.textMuted, fontSize: 13, marginTop: 2 }}>{m.email}</Text> : null}
                  </View>
                  <View style={{ backgroundColor: roleColor + '18', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5 }}>
                    <Text style={{ color: roleColor, fontSize: 12, fontWeight: '700', textTransform: 'capitalize' }}>{m.role}</Text>
                  </View>
                </View>

                {/* Controls row */}
                {canManage && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTopWidth: 1, borderTopColor: theme.border, gap: 20 }}>
                    {/* Role selector */}
                    {m.role !== 'owner' && (
                      <View style={{ flex: 1 }}>
                        <Text style={{ color: theme.textMuted, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Role</Text>
                        <View style={{ flexDirection: 'row', gap: 6 }}>
                          {ROLES.filter(r => r.value !== 'owner').map(r => {
                            const active = m.role === r.value;
                            return (
                              <TouchableOpacity
                                key={r.value}
                                disabled={active}
                                onPress={() => handleRoleChange(m.listener_id, r.value)}
                                style={{
                                  paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8,
                                  backgroundColor: active ? roleColor + '18' : 'transparent',
                                  borderWidth: 1,
                                  borderColor: active ? roleColor + '50' : theme.border,
                                }}
                              >
                                <Text style={{ color: active ? roleColor : theme.textMuted, fontSize: 12, fontWeight: active ? '700' : '500', textTransform: 'capitalize' }}>
                                  {r.value}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      </View>
                    )}

                    {/* Report frequency */}
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: theme.textMuted, fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Reports</Text>
                      <View style={{ flexDirection: 'row', gap: 6 }}>
                        {[{ v: 'daily', label: 'Daily' }, { v: 'weekly', label: 'Weekly' }, { v: 'none', label: 'Off' }].map(rf => {
                          const active = (m.report_frequency || 'daily') === rf.v;
                          const color = rf.v === 'none' ? '#ef4444' : '#16a34a';
                          return (
                            <TouchableOpacity
                              key={rf.v}
                              disabled={active}
                              onPress={() => handleReportFrequencyChange(m.listener_id, rf.v)}
                              style={{
                                paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8,
                                backgroundColor: active ? color + '14' : 'transparent',
                                borderWidth: 1,
                                borderColor: active ? color + '50' : theme.border,
                              }}
                            >
                              <Text style={{ color: active ? color : theme.textMuted, fontSize: 12, fontWeight: active ? '700' : '500' }}>{rf.label}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>

                    {/* Remove button */}
                    {m.role !== 'owner' && (
                      <TouchableOpacity
                        onPress={() => handleRemove(m.listener_id, m.username)}
                        style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: '#ef444430', backgroundColor: '#ef444408' }}
                      >
                        <FaTrash size={13} color="#ef4444" />
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}
      </>)}
    </ScrollView>
  );
}

function Field({ label, hint, theme, children }) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={[styles.fieldLabel, { color: theme.textSecondary }]}>{label}</Text>
      {hint && <Text style={[styles.fieldHint, { color: theme.textMuted }]}>{hint}</Text>}
      {children}
    </View>
  );
}

function ToggleField({ label, value, onChange, canEdit, theme }) {
  return (
    <View style={styles.toggleItem}>
      <Text style={{ color: theme.textPrimary, fontSize: 13, flex: 1 }}>{label}</Text>
      <Switch
        value={!!value}
        onValueChange={onChange}
        disabled={!canEdit}
        trackColor={{ false: theme.border, true: '#0066cc88' }}
        thumbColor={value ? '#0066cc' : '#ccc'}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, maxWidth: 640, alignSelf: 'center', width: '100%', paddingBottom: 60 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  title: { fontSize: 22, fontWeight: '700', marginBottom: 20 },
  readonlyBanner: { borderWidth: 1, borderRadius: 10, padding: 12, marginBottom: 20 },
  fieldWrap: { marginBottom: 22 },
  fieldLabel: { fontSize: 11, fontWeight: '700', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6 },
  fieldHint: { fontSize: 12, marginBottom: 8 },
  input: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, height: 42 },
  textarea: { height: 80, textAlignVertical: 'top' },
  flex1: { flex: 1 },
  disabled: { opacity: 0.5 },
  logoRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  logoPreview: { width: 52, height: 52, borderRadius: 10, flexShrink: 0 },
  logoPlaceholder: { justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderStyle: 'dashed' },
  // Language dropdown
  select: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    cursor: 'pointer',
  },
  langDropdown: {
    borderWidth: 1, borderRadius: 10, marginTop: 4,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },
  langSearchWrap: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 12, paddingVertical: 8,
    borderBottomWidth: 1,
  },
  langSearch: { flex: 1, fontSize: 13, outlineStyle: 'none' },
  langOption: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 14, paddingVertical: 9,
    cursor: 'pointer',
  },
  // Brand scale
  scaleRow: { flexDirection: 'row', gap: 10 },
  scaleChip: {
    flex: 1, borderWidth: 1.5, borderRadius: 10,
    paddingVertical: 10, paddingHorizontal: 12,
    alignItems: 'center', cursor: 'pointer',
  },
  // Generic chips (role, etc.)
  chipRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  chip: {
    paddingHorizontal: 16, paddingVertical: 8,
    borderRadius: 20, borderWidth: 1, cursor: 'pointer',
  },
  messageBanner: { borderWidth: 1, borderRadius: 8, padding: 12, marginBottom: 16 },
  saveBtn: { paddingVertical: 13, borderRadius: 10, alignItems: 'center', marginTop: 4 },
  saveBtnText: { color: '#fff', fontWeight: '600', fontSize: 15 },
  sectionDivider: { borderTopWidth: 1, marginVertical: 28 },
  memberRow: { flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderRadius: 12, padding: 12 },
  memberAvatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  roleBadge: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4 },
  inviteBox: { borderWidth: 1, borderRadius: 12, padding: 14, marginBottom: 16 },
  inviteBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0066cc', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8, minWidth: 80 },
  // AI Advisor Context
  advisorHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    borderWidth: 1, borderRadius: 10, padding: 14, cursor: 'pointer',
  },
  advisorBody: {
    borderWidth: 1, borderTopWidth: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10,
    padding: 16, marginTop: -1,
  },
  advisorSectionTitle: { fontSize: 13, fontWeight: '700', marginBottom: 8 },
  advisorFieldLabel: { fontSize: 11, fontWeight: '600', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 },
  toggleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  toggleItem: { flexDirection: 'row', alignItems: 'center', gap: 8, minWidth: 180 },
});
